import React, { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useApolloClient } from "@apollo/react-hooks";
import { GET_BLOCK_BY_ID, GET_PARAGRAPH_BY_ID } from "./queries";

export const GlobalContext = React.createContext();

/**
 * Devuelve el id del siguiente paso de una opción, o null si la opción es final
 * (enlace externo o sin pasos asociados).
 */
const getNextStepId = (option) =>
  option && option.fieldSelectLinkSteps === "steps"
    ? option.fieldWizardSteps?.[0]?.entityId ?? null
    : null;

const AppWrapper = (props) => {
  const [currentStep, setCurrentStep] = useState(null);
  const [currentOptions, setCurrentOptions] = useState(null);
  const [currentImage, setCurrentImage] = useState();
  const [defaultImage, setDefaultImage] = useState();
  const [initialDefaultImage, setInitialDefaultImage] = useState();
  const [imageOpacity, setImageOpacity] = useState(1);
  const [imageTransitionTimer, setImageTransitionTimer] = useState();
  const [wizardTheme, setWizardTheme] = useState();
  const [wizardTitle, setWizardTitle] = useState();
  const [wizardDescription, setWizardDescription] = useState();
  const [initialStep, setInitialStep] = useState(null);

  // ⭐ NUEVO: historial para poder volver atrás
  const [history, setHistory] = useState([]);
  // ⭐ NUEVO: pasos restantes detectados dinámicamente desde el paso actual
  const [remainingSteps, setRemainingSteps] = useState(0);
  const currentStepRef = useRef(currentStep);
  const depthCache = useRef(new Map());
  const client = useApolloClient();

  // Mantener la ref actualizada
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  const { blockid } = document.getElementById("wizard-block").dataset;
  const { error, data } = useQuery(GET_BLOCK_BY_ID, {
    variables: { id: blockid },
  });

  useEffect(() => {
    if (error) throw error;
    if (data) {
      const firstStep = data.blockContentById.fieldWizardSteps?.[0] ?? null;

      if (data.blockContentById.fieldImage) {
        setDefaultImage(data.blockContentById.fieldImage.url);
        setInitialDefaultImage(data.blockContentById.fieldImage.url);
      }

      setInitialStep((prev) => prev ?? firstStep);
      setCurrentStep(firstStep);

      if (data.blockContentById.fieldWizardTheme)
        setWizardTheme(data.blockContentById.fieldWizardTheme);

      if (data.blockContentById.fieldTitle)
        setWizardTitle(data.blockContentById.fieldTitle);

      if (data.blockContentById.fieldDescription)
        setWizardDescription(data.blockContentById.fieldDescription);
    }
  }, [error, data]);

  useEffect(() => {
    if (currentStep) setCurrentOptions(currentStep.fieldWizardOptions);
  }, [currentStep]);

  // ⭐ NUEVO: recorre el árbol de pasos para saber cuántos faltan desde uno dado.
  const measureRemainingDepth = useCallback(
    (step, visited) => {
      const nextIds = (step?.fieldWizardOptions ?? [])
        .map(getNextStepId)
        .filter((id) => id && !visited.has(id));

      if (!nextIds.length) return Promise.resolve(0);

      const branches = nextIds.map((id) => {
        const cached = depthCache.current.get(id);
        if (typeof cached === "number") return Promise.resolve(cached + 1);

        return client
          .query({ query: GET_PARAGRAPH_BY_ID, variables: { id } })
          .then(({ data: stepData }) =>
            measureRemainingDepth(
              stepData?.paragraphById,
              new Set([...visited, id])
            )
          )
          .then((depth) => {
            depthCache.current.set(id, depth);
            return depth + 1;
          })
          .catch(() => 1);
      });

      return Promise.all(branches).then((depths) => Math.max(...depths));
    },
    [client]
  );

  // ⭐ NUEVO: recalcular los pasos pendientes cada vez que cambia el paso actual.
  useEffect(() => {
    let cancelled = false;

    if (currentStep) {
      measureRemainingDepth(currentStep, new Set([currentStep.entityId]))
        .then((depth) => {
          if (!cancelled) setRemainingSteps(depth);
        })
        .catch(() => {
          if (!cancelled) setRemainingSteps(0);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [currentStep, measureRemainingDepth]);

  // ⭐ NUEVO: función para avanzar en el workflow
  const goToStep = useCallback((nextStep) => {
    setHistory((prev) => [...prev, currentStepRef.current]); // guardar paso anterior
    setCurrentStep(nextStep);                     // avanzar
  }, []);

  // ⭐ NUEVO: función para retroceder
  const goBack = useCallback(() => {
    if (history.length === 0) return;

    const previous = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1)); // quitar último
    setCurrentStep(previous);                 // regresar al paso anterior
  }, [history]);

  const resetWizard = useCallback(() => {
    if (!initialStep) return;

    clearTimeout(imageTransitionTimer);
    setHistory([]);
    setCurrentImage(null);
    setDefaultImage(initialDefaultImage);
    setCurrentStep(initialStep);
    setCurrentOptions(initialStep.fieldWizardOptions ?? null);
    setImageOpacity(1);
  }, [imageTransitionTimer, initialDefaultImage, initialStep]);

  // ⭐ NUEVO: posición actual y total detectado dinámicamente.
  const currentStepNumber = history.length + 1;
  const totalSteps = currentStepNumber + remainingSteps;

  return (
    <GlobalContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        currentOptions,
        currentImage,
        setCurrentImage,
        defaultImage,
        setDefaultImage,
        initialDefaultImage,
        imageOpacity,
        setImageOpacity,
        imageTransitionTimer,
        setImageTransitionTimer,
        wizardTheme,
        wizardTitle,
        wizardDescription,

        // ⭐ NUEVO
        history,
        goToStep,
        goBack,
        resetWizard,
        currentStepNumber,
        totalSteps,
      }}
    >
      {props.children}
    </GlobalContext.Provider>
  );
};

export default AppWrapper;
